import { debounce, isMobile, domBatcher, prefersReducedMotion, checkIsAboveFold } from "./utilities-BFAI3Dmv.js";
import { p as prepareEmblaInteraction, H as HybridCarousel, d as debouncedWatchResize, i as addEmblaAutoplayViewportLifecycle } from "./embla-extension-cVbn2auS.js";
import { A as Autoplay } from "./embla-carousel-autoplay.esm-lYa57aZD.js";
import { isMobileAnim } from "./mobile-anim-gate-Btn1wVuD.js";
import { unregisterSentryFlush, registerSentryFlush, queueAnim } from "./anim-batch-nQ-NAxq8.js";
import { getListingRevealController, scheduleDesignModeListingInit, parseLiquidJSON, setupListingEmblaFeatures, enableListingNavigation, enableListingDots, destroyListingEmbla, canSetupListingReveal, ensureListingRevealReadyForPlay } from "./listing-component-shared-B4yzcENT.js";
import { w as waapi } from "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
import "./native-scroll-snap-Cd15iSfl.js";
import "./mobile-autoplay-LQAitT2O.js";
import "./progressive-images-mIwHxxsh.js";
import "./autoplay-progress-BLL6tdZq.js";
const LISTING_REVEAL_ITEMS = "product-card, .product-listing-banner-slide > .banner";
class ProductListing extends HTMLElement {
  constructor() {
    super();
    this.emblaApi = null;
    this.cleanupFunctions = [];
    this.currentDeviceType = null;
    this._isFirstInit = true;
    this._lastEmblaViewportWidth = 0;
    this.nodes = {
      embla: null,
      viewport: null,
      container: null,
      prevBtns: [],
      nextBtns: [],
      dots: null
    };
    this.config = {
      showNavigation: false,
      showDots: false,
      options: {},
      autoPlay: {
        desktop: false,
        mobile: false
      }
    };
    this.handleResize = debounce(() => this.onResize(), 300);
  }
  isInteractionReady() {
    var _a;
    return !!this.emblaApi || !((_a = this.nodes) == null ? void 0 : _a.viewport);
  }
  prepareInteraction() {
    if (Shopify.designMode && !this.isInteractionReady()) {
      this.init();
      return this.isInteractionReady();
    }
    return prepareEmblaInteraction(this);
  }
  connectedCallback() {
    var _a;
    this.cacheNodes();
    this.parseConfig();
    this.currentDeviceType = isMobile() ? "mobile" : "desktop";
    const controller = getListingRevealController(this);
    if (!controller && !Shopify.designMode) {
      this._initStandalone();
    } else if (controller) {
      const isDesktopActive = controller.classList.contains("active");
      const isMobileActive = (_a = controller.closest(".megamenu-mobile-container")) == null ? void 0 : _a.classList.contains("is-megamenu-active");
      if (controller.tagName.toLowerCase() !== "slideshow-component" && (isDesktopActive || isMobileActive)) {
        if (controller.classList.contains("megamenu-v2")) {
          const token = this._tabSetupToken;
          domBatcher.write(() => {
            var _a2;
            if (this._tabSetupToken === token) (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        } else {
          const token = this._tabSetupToken;
          domBatcher.write(() => {
            var _a2;
            if (this._tabSetupToken === token) (_a2 = this.reset) == null ? void 0 : _a2.call(this);
          });
        }
      } else if ([
        "custom-dialog",
        "cart-drawer",
        "quick-view-dialog",
        "custom-div-popup"
      ].includes(controller.tagName.toLowerCase())) {
        const dialog = controller.querySelector("dialog");
        if (dialog && dialog.open) {
          domBatcher.write(() => {
            var _a2;
            return (_a2 = this.play) == null ? void 0 : _a2.call(this);
          });
        }
        const openHandler = () => domBatcher.write(() => {
          var _a2;
          return (_a2 = this.play) == null ? void 0 : _a2.call(this);
        });
        const closeHandler = () => domBatcher.write(() => {
          var _a2;
          return (_a2 = this.reset) == null ? void 0 : _a2.call(this);
        });
        controller.addEventListener("dialog:open", openHandler);
        controller.addEventListener("dialog:closing", closeHandler);
        if (typeof this.registerCleanup === "function") {
          this.registerCleanup(() => {
            controller.removeEventListener("dialog:open", openHandler);
            controller.removeEventListener("dialog:closing", closeHandler);
          });
        }
      }
    }
    if (Shopify.designMode) {
      scheduleDesignModeListingInit(this);
    }
    window.addEventListener("resize", this.handleResize);
  }
  disconnectedCallback() {
    var _a, _b, _c, _d, _e;
    window.removeEventListener("resize", this.handleResize);
    (_a = this._foldIO) == null ? void 0 : _a.disconnect();
    (_b = this._scrollObs) == null ? void 0 : _b.disconnect();
    (_c = this._prewarmObs) == null ? void 0 : _c.disconnect();
    (_d = this._emblaInitObserver) == null ? void 0 : _d.disconnect();
    if (this._introTrigger) {
      document.removeEventListener("theme:intro:complete", this._introTrigger);
      this._introTrigger = null;
    }
    if (this._sentryCallback) {
      unregisterSentryFlush(this._sentryCallback);
      this._sentryCallback = null;
    }
    (_e = this.reset) == null ? void 0 : _e.call(this);
    this.destroy();
  }
  // ========== DOM & Configuration ==========
  cacheNodes() {
    this.nodes.embla = this.querySelector(".embla");
    this.nodes.viewport = this.querySelector(".embla__viewport");
    this.nodes.container = this.querySelector(".embla__container");
    this.nodes.prevBtns = Array.from(
      this.querySelectorAll(".embla__snip-button--prev button")
    ).filter((el) => !el.closest(".embla__viewport"));
    this.nodes.nextBtns = Array.from(
      this.querySelectorAll(".embla__snip-button--next button")
    ).filter((el) => !el.closest(".embla__viewport"));
    const allDots = Array.from(this.querySelectorAll(".embla__snip-dots"));
    this.nodes.dots = allDots.find((el) => !el.closest(".embla__viewport")) || null;
  }
  parseConfig() {
    this.config.showNavigation = this.dataset.showNavigation === "true";
    this.config.showDots = this.dataset.showDots === "true";
    this.config.options = this.parseJSON(this.dataset.options, {});
    const autoPlayDesktop = this.parseJSON(this.dataset.autoPlayDesktop, false);
    const autoPlayMobile = this.parseJSON(this.dataset.autoPlayMobile, false);
    this.config.autoPlay.desktop = autoPlayDesktop || false;
    this.config.autoPlay.mobile = autoPlayMobile || autoPlayDesktop;
  }
  parseJSON(jsonString, fallback) {
    return parseLiquidJSON(jsonString, fallback, "ProductListing");
  }
  getCurrentAutoPlayConfig() {
    return isMobile() ? this.config.autoPlay.mobile : this.config.autoPlay.desktop;
  }
  // ========== Initialization ==========
  init() {
    this.destroy();
    this.initEmbla();
  }
  initEmbla() {
    if (Shopify.designMode) {
      this._doInitEmbla();
    } else {
      if (this._emblaInitQueued) return;
      this._emblaInitQueued = true;
      this._emblaInitObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this._emblaInitObserver.disconnect();
            this._emblaInitObserver = null;
            this._emblaInitQueued = false;
            if (!this.isConnected) return;
            this._doInitEmbla();
          }
        },
        { rootMargin: "200px" }
      );
      this._emblaInitObserver.observe(this);
    }
  }
  _doInitEmbla() {
    var _a;
    if (!this.nodes.viewport || this.emblaApi) return;
    const autoPlayConfig = this.getCurrentAutoPlayConfig();
    const plugins = [];
    if (autoPlayConfig && !prefersReducedMotion()) {
      plugins.push(
        Autoplay({
          delay: autoPlayConfig.delay ?? 5e3,
          // The viewport lifecycle starts storefront autoplay only when useful.
          // Theme Editor stays eager so merchants retain the existing preview.
          playOnInit: Shopify.designMode,
          stopOnInteraction: autoPlayConfig.stopOnInteraction ?? false,
          stopOnMouseEnter: autoPlayConfig.stopOnMouseEnter ?? true,
          stopOnFocusIn: false
        })
      );
    }
    try {
      this.emblaApi = HybridCarousel(
        this.nodes.viewport,
        { ...this.config.options, watchResize: debouncedWatchResize() },
        plugins
      );
      this._autoplayViewportCleanup = addEmblaAutoplayViewportLifecycle(
        this.emblaApi,
        this
      );
      this._lastEmblaViewportWidth = ((_a = this.nodes.viewport) == null ? void 0 : _a.clientWidth) || 0;
      this.featureCleanups = [];
      this.setupEmblaFeatures();
      this.emblaApi.on("reInit", () => this.setupEmblaFeatures());
      this._isFirstInit = false;
    } catch (error) {
    }
  }
  setupEmblaFeatures() {
    setupListingEmblaFeatures(this, {
      deferControlWrites: true,
      toggleDisabled: true
    });
  }
  enableNavigation() {
    enableListingNavigation(this);
  }
  enableDots() {
    enableListingDots(this);
  }
  // ========== Public API (Theme Editor) ==========
  scrollToSlide(slideIndex) {
    var _a;
    if (!this.isInteractionReady()) this.prepareInteraction();
    (_a = this.emblaApi) == null ? void 0 : _a.scrollTo(slideIndex);
  }
  // ========== Resize ==========
  onResize() {
    const newDevice = isMobile() ? "mobile" : "desktop";
    if (this.currentDeviceType !== newDevice) {
      this.currentDeviceType = newDevice;
      this.init();
    }
  }
  // ========== Cleanup ==========
  registerCleanup(fn) {
    if (fn && typeof fn === "function") this.cleanupFunctions.push(fn);
  }
  destroy() {
    var _a, _b;
    (_a = this._emblaInitObserver) == null ? void 0 : _a.disconnect();
    this._emblaInitObserver = null;
    this._emblaInitQueued = false;
    (_b = this._autoplayViewportCleanup) == null ? void 0 : _b.call(this);
    this._autoplayViewportCleanup = null;
    destroyListingEmbla(this);
  }
  // ========== Tab Group Animation API ==========
  _initStandalone() {
    const token = (this._standaloneInitToken || 0) + 1;
    this._standaloneInitToken = token;
    if (prefersReducedMotion() || isMobileAnim || document.body.dataset.animationOnScroll !== "true") {
      if (typeof this.init === "function") this.init();
      return;
    }
    this._foldIO = new IntersectionObserver(
      (entries) => {
        this._foldIO.disconnect();
        this._foldIO = null;
        domBatcher.read(() => {
          if (!this.isConnected || this._standaloneInitToken !== token) return;
          const revealEligible = !checkIsAboveFold(entries[0]) && canSetupListingReveal(this);
          domBatcher.write(() => {
            if (!this.isConnected || this._standaloneInitToken !== token) return;
            if (!revealEligible) {
              if (typeof this.init === "function") this.init();
              return;
            }
            if (!this.setup({ revealEligible })) {
              if (this._isFirstInit && typeof this.init === "function")
                this.init();
              return;
            }
            this._prewarmObs = new IntersectionObserver(
              (entries2) => {
                if (entries2[0].isIntersecting) {
                  this._prewarmObs.disconnect();
                  this._prewarmObs = null;
                  if (this._isFirstInit && typeof this.init === "function")
                    this.init();
                }
              },
              { rootMargin: "0px 0px 50% 0px" }
            );
            this._prewarmObs.observe(this);
            this._scrollObs = new IntersectionObserver(
              (entries2) => {
                if (entries2[0].isIntersecting) {
                  this._scrollObs.disconnect();
                  this._scrollObs = null;
                  this.play();
                }
              },
              { rootMargin: "0px 0px -10% 0px" }
            );
            this._scrollObs.observe(this);
            this._sentryCallback = () => {
              const obs = this._scrollObs || this._animObserver;
              if (obs) {
                const checkObs = new IntersectionObserver(
                  (checkEntries) => {
                    checkObs.disconnect();
                    if (checkEntries[0].isIntersecting) {
                      const currentObs = this._scrollObs || this._animObserver;
                      if (currentObs) {
                        currentObs.disconnect();
                        if (this._scrollObs) this._scrollObs = null;
                        if (this._animObserver) this._animObserver = null;
                        if (typeof this.play === "function") this.play();
                      }
                    }
                  },
                  { threshold: 0 }
                );
                checkObs.observe(this);
              }
            };
            registerSentryFlush(this._sentryCallback);
          });
        });
      },
      { threshold: 0 }
    );
    this._foldIO.observe(this);
  }
  setup({ revealEligible } = {}) {
    this._standaloneInitToken = (this._standaloneInitToken || 0) + 1;
    if (prefersReducedMotion() || isMobileAnim) return false;
    if (this._ready) return true;
    if (!(revealEligible ?? canSetupListingReveal(this))) return false;
    const cards = Array.from(this.querySelectorAll(LISTING_REVEAL_ITEMS));
    cards.forEach((card) => {
      card.style.willChange = "transform, opacity";
      card.style.opacity = "0";
      card.style.transform = "translateY(30px)";
    });
    this._cards = cards;
    this._ready = true;
    return true;
  }
  play(delayMs = 0) {
    var _a;
    const wasFirstInit = this._isFirstInit;
    if (this.emblaApi && !wasFirstInit) {
      requestAnimationFrame(() => {
        var _a2;
        if (!this.emblaApi) return;
        if (this.closest(".megamenu-v2")) {
          const width = ((_a2 = this.nodes.viewport) == null ? void 0 : _a2.clientWidth) || 0;
          if (!width || width === this._lastEmblaViewportWidth) return;
          this._lastEmblaViewportWidth = width;
        }
        this.emblaApi.reInit();
      });
    }
    if (prefersReducedMotion() || isMobileAnim) {
      if (this._isFirstInit) {
        if (document.readyState === "complete") {
          if (typeof window.requestIdleCallback === "function") {
            requestIdleCallback(() => {
              if (this.isConnected) this.init();
            });
          } else {
            setTimeout(() => {
              if (this.isConnected) this.init();
            }, 100);
          }
        } else {
          window.addEventListener(
            "load",
            () => {
              if (typeof window.requestIdleCallback === "function") {
                requestIdleCallback(() => {
                  if (this.isConnected) this.init();
                });
              } else {
                setTimeout(() => {
                  if (this.isConnected) this.init();
                }, 100);
              }
            },
            { once: true }
          );
        }
      }
      return;
    }
    if (!ensureListingRevealReadyForPlay(this)) {
      if (this._isFirstInit && typeof this.init === "function") this.init();
      return;
    }
    const cards = Array.from(this.querySelectorAll(LISTING_REVEAL_ITEMS));
    if (cards.length === 0) return;
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    const baseDelay = delayMs || 0;
    const staggerAmount = 60;
    const duration = 600;
    const easing = "cubic-bezier(0.16, 1, 0.3, 1)";
    clearTimeout(this._failsafeTimer);
    this._failsafeTimer = null;
    const self = this;
    const animateCards = (computedDelay) => {
      const lastCardDelay = Math.max(cards.length - 1, 0) * staggerAmount;
      const maxDuration = computedDelay + lastCardDelay + duration;
      clearTimeout(self._failsafeTimer);
      self._failsafeTimer = setTimeout(() => self.reset(), maxDuration + 200);
      try {
        self._anim = waapi.animate(cards, {
          opacity: [
            0,
            1
          ],
          transform: [
            "translateY(30px)",
            "translateY(0px)"
          ],
          duration,
          delay: (_, i) => computedDelay + i * staggerAmount,
          ease: easing,
          composition: "replace",
          onComplete: () => {
            clearTimeout(self._failsafeTimer);
            self._failsafeTimer = null;
            self._anim = null;
            cards.forEach((card) => {
              card.style.removeProperty("opacity");
              card.style.removeProperty("transform");
              card.style.removeProperty("will-change");
            });
            if (self._isFirstInit) {
              self.init();
            }
          }
        });
      } catch (err) {
        self.reset();
      }
    };
    const controller = getListingRevealController(this);
    if (controller) {
      animateCards(baseDelay);
    } else {
      queueAnim({
        el: this,
        isStandalone: true,
        baseDelay,
        staggerAmount: 0,
        animate: animateCards
      });
    }
    this._cards = cards;
  }
  reset() {
    var _a;
    this._standaloneInitToken = (this._standaloneInitToken || 0) + 1;
    if (prefersReducedMotion() || isMobileAnim) {
      if (this._isFirstInit) {
        if (document.readyState === "complete") {
          if (typeof window.requestIdleCallback === "function") {
            requestIdleCallback(() => {
              if (this.isConnected) this.init();
            });
          } else {
            setTimeout(() => {
              if (this.isConnected) this.init();
            }, 100);
          }
        } else {
          window.addEventListener(
            "load",
            () => {
              if (typeof window.requestIdleCallback === "function") {
                requestIdleCallback(() => {
                  if (this.isConnected) this.init();
                });
              } else {
                setTimeout(() => {
                  if (this.isConnected) this.init();
                }, 100);
              }
            },
            { once: true }
          );
        }
      }
      return;
    }
    clearTimeout(this._failsafeTimer);
    this._failsafeTimer = null;
    try {
      (_a = this._anim) == null ? void 0 : _a.revert();
    } catch (e) {
    }
    this._anim = null;
    const cards = this._cards || Array.from(this.querySelectorAll(LISTING_REVEAL_ITEMS));
    cards.forEach((card) => {
      card.style.removeProperty("opacity");
      card.style.removeProperty("transform");
      card.style.removeProperty("will-change");
    });
    if (this._isFirstInit) {
      if (document.readyState === "complete") {
        if (typeof window.requestIdleCallback === "function") {
          requestIdleCallback(() => {
            if (this.isConnected) this.init();
          });
        } else {
          setTimeout(() => {
            if (this.isConnected) this.init();
          }, 100);
        }
      } else {
        window.addEventListener(
          "load",
          () => {
            if (typeof window.requestIdleCallback === "function") {
              requestIdleCallback(() => {
                if (this.isConnected) this.init();
              });
            } else {
              setTimeout(() => {
                if (this.isConnected) this.init();
              }, 100);
            }
          },
          { once: true }
        );
      }
    }
    this._ready = false;
  }
}
if (!customElements.get("product-listing-component")) {
  customElements.define("product-listing-component", ProductListing);
}
